import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { smartUpload } from "@/lib/upload";
import { UploadProgress } from "@/components/UploadProgress";
import { FolderBreadcrumb, lastSegment, parentPath } from "@/components/FolderBreadcrumb";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/components/badges";
import { useTranslation } from "react-i18next";
import { Archive, Download, Folder, FolderPlus, FolderUp, Loader2, MoveRight, Pencil, Share2, Trash2, Upload } from "lucide-react";
import { FolderPickerDialog } from "@/components/FolderPickerDialog";
import { useConfirm } from "@/components/ConfirmDialog";

const DRAG_FILE_TYPE = "application/x-pruva-file";
const isInternalDrag = (e) => e.dataTransfer.types.includes(DRAG_FILE_TYPE);

const fmtSize = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

async function saveBlob(res, filename) {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function ProjectFileSharing() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { t } = useTranslation();
  const { selectedId } = useProject();
  const qc = useQueryClient();
  const [company, setCompany] = useState(null); // party_id or "shared"
  const [folder, setFolder] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [shareTarget, setShareTarget] = useState(null); // {kind:'file'|'folder', file?, path?}
  const [sharePicks, setSharePicks] = useState([]);
  const [renameTarget, setRenameTarget] = useState(null); // {kind:'file'|'folder', file?, path?}
  const [renameName, setRenameName] = useState("");
  const [moveFile, setMoveFile] = useState(null);
  const fileRef = useRef(null);
  const dirRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  const [dropFolder, setDropFolder] = useState(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["project-fs", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/fs`)).data,
    enabled: !!selectedId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["project-fs", selectedId] });

  const renameFileMut = useMutation({
    mutationFn: async () => (await api.put(`/fs/files/${renameTarget.file.id}/rename`, { filename: renameName })).data,
    onSuccess: () => { toast.success(t("common.renamedToast")); setRenameTarget(null); setRenameName(""); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const renameFolderMut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${selectedId}/fs/folders/rename`, { party_id: activeCompany, path: renameTarget.path, new_name: renameName })).data,
    onSuccess: (d) => {
      toast.success(t("common.renamedToast"));
      if (folder === renameTarget.path || folder.startsWith(renameTarget.path + "/")) setFolder(d.path + folder.slice(renameTarget.path.length));
      setRenameTarget(null);
      setRenameName("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const moveFileMut = useMutation({
    mutationFn: async ({ id, target }) => api.put(`/fs/files/${id}/move`, { folder: target }),
    onSuccess: () => { toast.success(t("driveExplorer.fileMovedToast")); setMoveFile(null); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const selectedWritableIds = () => [...selected].filter((id) => companyFiles.find((x) => x.id === id)?.writable);

  const bulkMoveMut = useMutation({
    mutationFn: async (target) => {
      const ids = selectedWritableIds();
      for (const id of ids) await api.put(`/fs/files/${id}/move`, { folder: target });
      return ids.length;
    },
    onSuccess: (n) => { toast.success(t("driveExplorer.movedSelectedToast", { count: n })); setSelected(new Set()); setBulkMoveOpen(false); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const bulkDeleteMut = useMutation({
    mutationFn: async () => {
      const ids = selectedWritableIds();
      for (const id of ids) await api.delete(`/fs/files/${id}`);
      return ids.length;
    },
    onSuccess: (n) => { toast.success(t("driveExplorer.deletedSelectedToast", { count: n })); setSelected(new Set()); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });
  const onErr = (e) => toast.error(fmtErr(e));

  const myIds = useMemo(() => new Set(data?.my_party_ids || []), [data]);
  const companies = data?.companies || [];
  const activeCompany = company || companies.find((c) => c.writable)?.party_id || companies[0]?.party_id || null;
  const activeInfo = companies.find((c) => c.party_id === activeCompany);
  const writable = !!activeInfo?.writable;

  const companyFolders = useMemo(
    () => (data?.folders || []).filter((f) => f.party_id === activeCompany).map((f) => f.path).sort(),
    [data, activeCompany]
  );
  const companyFiles = useMemo(
    () => (data?.files || []).filter((f) => f.party_id === activeCompany && f.is_latest !== false),
    [data, activeCompany]
  );
  const childFolders = useMemo(() => companyFolders.filter((p) => parentPath(p) === folder), [companyFolders, folder]);
  const visibleFiles = useMemo(() => companyFiles.filter((f) => (f.folder || "") === folder), [companyFiles, folder]);
  const subtreeCount = (p) => companyFiles.filter((f) => (f.folder || "") === p || (f.folder || "").startsWith(p + "/")).length;
  const hasContents = (p) => subtreeCount(p) > 0 || companyFolders.some((x) => x !== p && x.startsWith(p + "/"));
  const sharedWithMe = useMemo(
    () => (data?.files || []).filter((f) => !myIds.has(f.party_id) && f.is_latest !== false),
    [data, myIds]
  );
  const sharedFoldersWithMe = useMemo(
    () => (data?.folders || []).filter((f) => !myIds.has(f.party_id)),
    [data, myIds]
  );
  const showSharedTab = !data?.can_see_all && (sharedWithMe.length > 0 || sharedFoldersWithMe.length > 0);
  const viewingShared = company === "shared";

  const uploadMut = useMutation({
    mutationFn: async () => {
      const totalBytes = uploadFiles.reduce((a, f) => a + f.size, 0);
      const started = performance.now();
      let loadedAll = 0;
      for (const f of uploadFiles) {
        const targetFolder = [folder, f._relDir || ""].filter(Boolean).join("/");
        await smartUpload(
          f,
          { party_id: activeCompany, folder: targetFolder },
          (p) => {
            const loaded = loadedAll + p.loaded;
            const el = (performance.now() - started) / 1000;
            const speed = el > 0 ? loaded / el : 0;
            setUploadProgress({ percent: Math.round((loaded / totalBytes) * 100), loaded, total: totalBytes, speed, eta: speed > 0 ? (totalBytes - loaded) / speed : null });
          },
          `/projects/${selectedId}/fs/uploads`
        );
        loadedAll += f.size;
      }
    },
    onSuccess: () => {
      toast.success(t("sharing.uploadedToast", { count: uploadFiles.length }));
      setUploadOpen(false);
      setUploadFiles([]);
      setUploadProgress(null);
      invalidate();
    },
    onError: (e) => {
      toast.error(fmtErr(e));
      setUploadProgress(null);
    },
  });

  const newFolderMut = useMutation({
    mutationFn: async () => api.post(`/projects/${selectedId}/fs/folders`, { party_id: activeCompany, path: [folder, newFolderName.trim()].filter(Boolean).join("/") }),
    onSuccess: (r) => {
      toast.success(t("sharing.folderCreatedToast", { path: r.data.path }));
      setNewFolderOpen(false);
      setNewFolderName("");
      invalidate();
    },
    onError: onErr,
  });

  const delFolderMut = useMutation({
    mutationFn: async (path) => api.delete(`/projects/${selectedId}/fs/folders`, { params: { party_id: activeCompany, path } }),
    onSuccess: (_d, path) => {
      toast.success(t("sharing.folderDeletedToast"));
      if (folder === path || folder.startsWith(path + "/")) setFolder(parentPath(path));
      invalidate();
    },
    onError: onErr,
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/fs/files/${id}`),
    onSuccess: () => { toast.success(t("sharing.fileDeletedToast")); invalidate(); },
    onError: onErr,
  });

  const shareMut = useMutation({
    mutationFn: async () => {
      if (shareTarget.kind === "file") {
        return api.post(`/fs/files/${shareTarget.file.id}/share`, { party_ids: sharePicks });
      }
      return api.post(`/projects/${selectedId}/fs/folders/share`, { party_id: activeCompany, path: shareTarget.path, party_ids: sharePicks });
    },
    onSuccess: () => {
      toast.success(t("sharing.shareUpdatedToast"));
      setShareTarget(null);
      setSharePicks([]);
      invalidate();
    },
    onError: onErr,
  });

  const download = async (f) => {
    try {
      const res = await api.get(`/fs/files/${f.id}/download`, { responseType: "blob" });
      await saveBlob(res, f.filename);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const zipSelected = async () => {
    try {
      const res = await api.post(`/projects/${selectedId}/fs/zip`, { file_ids: [...selected] }, { responseType: "blob" });
      await saveBlob(res, "project-files.zip");
      toast.success(t("sharing.zippedToast", { count: selected.size }));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const pickFiles = (list, isDir) => {
    const arr = Array.from(list || []);
    arr.forEach((f) => {
      const rel = f.webkitRelativePath || "";
      f._relDir = isDir && rel.includes("/") ? rel.split("/").slice(0, -1).join("/") : "";
    });
    setUploadFiles(arr);
    setUploadOpen(true);
  };

  const traverseEntry = async (entry, prefix = "") => {
    if (entry.isFile) {
      const file = await new Promise((res, rej) => entry.file(res, rej));
      file._relDir = prefix;
      return [file];
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      let entries = [];
      let batch;
      do {
        batch = await new Promise((res, rej) => reader.readEntries(res, rej));
        entries = entries.concat(batch);
      } while (batch.length > 0);
      const sub = await Promise.all(entries.map((e) => traverseEntry(e, prefix ? `${prefix}/${entry.name}` : entry.name)));
      return sub.flat();
    }
    return [];
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    if (!writable) return;
    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
    let files = [];
    if (entries.length) {
      const nested = await Promise.all(entries.map((en) => traverseEntry(en)));
      files = nested.flat();
    } else {
      files = Array.from(e.dataTransfer.files || []);
      files.forEach((f) => { f._relDir = ""; });
    }
    if (!files.length) return;
    setUploadFiles(files);
    setUploadOpen(true);
  };

  const toggleSharePick = (pid) => setSharePicks((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));
  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

  if (isLoading || !data) {
    return <p className="text-sm text-slate-500" data-testid="sharing-loading">{t("common.loading")}</p>;
  }

  return (
    <div
      className="relative space-y-4"
      data-testid="sharing-page"
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={(e) => { e.preventDefault(); if (isInternalDrag(e)) return; dragCounter.current += 1; setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); if (isInternalDrag(e)) return; dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); } }}
      onDrop={(e) => { if (isInternalDrag(e)) { e.preventDefault(); setDropFolder(null); return; } handleDrop(e); }}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-sky-400 bg-sky-50/80" data-testid="sharing-dropzone-overlay">
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-700">
            <Upload className="h-5 w-5" /> {t("driveExplorer.dropHere")}
          </div>
        </div>
      )}
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">{t("sharing.title")}</h1>
        <p className="text-sm text-slate-500">{t("sharing.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="sharing-companies">
        {companies.map((c) => (
          <Button key={c.party_id} size="sm" variant={!viewingShared && activeCompany === c.party_id ? "default" : "outline"} className={!viewingShared && activeCompany === c.party_id ? "bg-slate-900" : ""} onClick={() => { setCompany(c.party_id); setFolder(""); setSelected(new Set()); }} data-testid={`share-company-${c.party_id}`}>
            <span className="font-mono font-bold">{c.code || c.company_name}</span>
            <span className="ml-1.5 hidden text-xs opacity-70 sm:inline">{c.company_name}</span>
          </Button>
        ))}
        {showSharedTab && (
          <Button size="sm" variant={viewingShared ? "default" : "outline"} className={viewingShared ? "bg-sky-600" : "border-sky-300 text-sky-700"} onClick={() => { setCompany("shared"); setSelected(new Set()); }} data-testid="shared-with-me-tab">
            {t("sharing.sharedWithMe", { count: sharedWithMe.length + sharedFoldersWithMe.length })}
          </Button>
        )}
      </div>

      {!viewingShared && activeCompany && (
        <Card>
          <CardContent className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <FolderBreadcrumb root={activeInfo?.code || activeInfo?.company_name || "Root"} path={folder} onNavigate={setFolder} testidPrefix="fs" />
              <div className="ml-auto flex gap-2">
                {selected.size > 0 && (
                  <Button size="sm" variant="outline" onClick={zipSelected} data-testid="fs-zip-selected">
                    <Archive className="mr-1.5 h-4 w-4" /> {t("driveExplorer.zipSelected", { count: selected.size })}
                  </Button>
                )}
                {writable && selected.size > 0 && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setBulkMoveOpen(true)} data-testid="fs-move-selected">
                      <MoveRight className="mr-1.5 h-4 w-4" /> {t("driveExplorer.moveSelected", { count: selected.size })}
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => askConfirm(t("driveExplorer.deleteSelectedConfirm", { count: selected.size }), () => bulkDeleteMut.mutate())} data-testid="fs-delete-selected">
                      <Trash2 className="mr-1.5 h-4 w-4" /> {t("driveExplorer.deleteSelected", { count: selected.size })}
                    </Button>
                  </>
                )}
                {writable && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setNewFolderOpen(true)} data-testid="fs-new-folder">
                      <FolderPlus className="mr-1.5 h-4 w-4" /> {t("sharing.newSubfolder")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => dirRef.current?.click()} data-testid="fs-upload-folder-button">
                      <FolderUp className="mr-1.5 h-4 w-4" /> {t("sharing.uploadFolder")}
                    </Button>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => fileRef.current?.click()} data-testid="fs-upload-button">
                      <Upload className="mr-1.5 h-4 w-4" /> {t("sharing.uploadFiles")}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { pickFiles(e.target.files, false); e.target.value = ""; }} data-testid="fs-file-input" />
            <input ref={dirRef} type="file" className="hidden" {...{ webkitdirectory: "" }} onChange={(e) => { pickFiles(e.target.files, true); e.target.value = ""; }} data-testid="fs-dir-input" />

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.version")}</TableHead>
                    <TableHead>{t("common.size")}</TableHead>
                    <TableHead>{t("common.uploaded")}</TableHead>
                    <TableHead>{t("sharing.thSharedWith")}</TableHead>
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {childFolders.length === 0 && visibleFiles.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500" data-testid="fs-empty">{t("driveExplorer.empty")}</TableCell></TableRow>
                  )}
                  {childFolders.map((fp) => {
                    const fobj = (data.folders || []).find((x) => x.party_id === activeCompany && x.path === fp);
                    const full = hasContents(fp);
                    return (
                      <TableRow
                        key={fp}
                        className={`cursor-pointer hover:bg-slate-50 ${dropFolder === fp ? "bg-sky-50 ring-1 ring-inset ring-sky-300" : ""}`}
                        onClick={() => setFolder(fp)}
                        onDragOver={(e) => { if (isInternalDrag(e)) { e.preventDefault(); e.stopPropagation(); setDropFolder(fp); } }}
                        onDragLeave={() => setDropFolder((d) => (d === fp ? null : d))}
                        onDrop={(e) => { if (!isInternalDrag(e)) return; e.preventDefault(); e.stopPropagation(); setDropFolder(null); const id = e.dataTransfer.getData(DRAG_FILE_TYPE); const src = companyFiles.find((x) => x.id === id); if (!src || !src.writable || (src.folder || "") === fp) return; moveFileMut.mutate({ id, target: fp }); }}
                        data-testid={`fs-folder-row-${slug(fp)}`}
                      >
                        <TableCell></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 font-medium text-slate-900">
                            <Folder className="h-4 w-4 text-amber-500" /> {lastSegment(fp)}
                            <span className="font-mono text-[10px] text-slate-400">{t("common.filesCount", { count: subtreeCount(fp) })}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">—</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">—</TableCell>
                        <TableCell className="text-xs text-slate-400">{t("badges.folder")}</TableCell>
                        <TableCell>
                          {fobj?.shared_with_names?.length > 0 ? (
                            <div className="flex flex-wrap gap-1">{fobj.shared_with_names.map((n) => <Badge key={n} variant="outline" className="bg-sky-50 font-mono text-[10px] text-sky-700">{n}</Badge>)}</div>
                          ) : <span className="text-xs text-slate-400">—</span>}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {writable && (
                              <>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-sky-600" title={t("common.renameFolder")} onClick={() => { setRenameTarget({ kind: "folder", path: fp }); setRenameName(lastSegment(fp)); }} data-testid={`fs-folder-rename-${slug(fp)}`}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-sky-600" title={t("sharing.shareFolderT")} onClick={() => { setShareTarget({ kind: "folder", path: fp }); setSharePicks(fobj?.shared_with || []); }} data-testid={`fs-share-folder-${slug(fp)}`}>
                                  <Share2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-rose-600 hover:text-rose-700 disabled:opacity-40"
                                  title={full ? t("sharing.folderNotEmptyT") : t("sharing.deleteEmptyFolderT")}
                                  disabled={full || delFolderMut.isPending}
                                  onClick={() => askConfirm(t("sharing.deleteFolderConfirm", { name: lastSegment(fp) }), () => delFolderMut.mutate(fp))}
                                  data-testid={`fs-folder-delete-${slug(fp)}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visibleFiles.map((f) => (
                    <TableRow key={f.id} draggable={!!f.writable} onDragStart={(e) => { e.dataTransfer.setData(DRAG_FILE_TYPE, f.id); e.dataTransfer.effectAllowed = "move"; }} data-testid={`fs-file-row-${f.id}`}>
                      <TableCell><Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggleSelect(f.id)} data-testid={`fs-select-${f.id}`} /></TableCell>
                      <TableCell className="font-medium text-slate-900">{f.filename}</TableCell>
                      <TableCell className="font-mono text-xs font-semibold">{f.version}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtSize(f.size)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{f.uploaded_by_email} · {fmtDateTime(f.uploaded_at)}</TableCell>
                      <TableCell>
                        {(f.shared_with_names || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1">{f.shared_with_names.map((n) => <Badge key={n} variant="outline" className="bg-sky-50 font-mono text-[10px] text-sky-700">{n}</Badge>)}</div>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title={t("library.downloadT")} onClick={() => download(f)} data-testid={`fs-download-${f.id}`}><Download className="h-4 w-4" /></Button>
                          {f.writable && (
                            <>
                              <Button variant="ghost" size="icon" title={t("common.renameFile")} onClick={() => { setRenameTarget({ kind: "file", file: f }); setRenameName(f.filename); }} data-testid={`fs-rename-${f.id}`}><Pencil className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" title={t("sharing.shareWithT")} onClick={() => { setShareTarget({ kind: "file", file: f }); setSharePicks(f.shared_with || []); }} data-testid={`fs-share-${f.id}`}><Share2 className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" title={t("library.moveT")} onClick={() => setMoveFile(f)} data-testid={`fs-move-${f.id}`}><MoveRight className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="icon" title={t("common.delete")} className="text-rose-600" onClick={() => askConfirm(t("sharing.deleteConfirm", { filename: f.filename }), () => deleteMut.mutate(f.id))} data-testid={`fs-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {viewingShared && (
        <Card data-testid="shared-with-me-panel">
          <CardContent className="space-y-4 p-4">
            {sharedFoldersWithMe.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("sharing.foldersSharedWithYou")}</p>
                {sharedFoldersWithMe.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm" data-testid={`shared-folder-row-${f.id}`}>
                    <Share2 className="h-4 w-4 text-sky-600" />
                    <span className="font-medium">{f.company_name}</span>
                    <span className="font-mono text-xs text-slate-500">/ {f.path}</span>
                    <span className="text-xs text-slate-400">{t("sharing.includesFuture")}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>{t("common.file")}</TableHead><TableHead>{t("admin.thCompany")}</TableHead><TableHead>{t("common.folder")}</TableHead><TableHead>{t("common.size")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {sharedWithMe.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500">{t("sharing.nothingShared")}</TableCell></TableRow>}
                  {sharedWithMe.map((f) => (
                    <TableRow key={f.id} data-testid={`shared-file-row-${f.id}`}>
                      <TableCell className="font-medium text-slate-900">{f.filename}</TableCell>
                      <TableCell className="text-xs">{f.company_name}</TableCell>
                      <TableCell className="font-mono text-xs">{lastSegment(f.folder || "") || t("sharing.root")}</TableCell>
                      <TableCell className="font-mono text-xs">{fmtSize(f.size)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" title={t("library.downloadT")} onClick={() => download(f)} data-testid={`fs-download-${f.id}`}><Download className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploadMut.isPending) { setUploadOpen(o); if (!o) { setUploadFiles([]); setUploadProgress(null); } } }}>
        <DialogContent data-testid="fs-upload-dialog">
          <DialogHeader><DialogTitle>{t("sharing.uploadTo", { company: activeInfo?.company_name })}{folder ? ` / ${folder}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-slate-600">{t("sharing.filesSelected", { count: uploadFiles.length })}</p>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-xs text-slate-500">
              {uploadFiles.slice(0, 20).map((f, i) => <li key={`${f._relDir || ""}/${f.name}-${f.size}`} className="font-mono">{(f._relDir ? f._relDir + "/" : "") + f.name} ({fmtSize(f.size)})</li>)}
              {uploadFiles.length > 20 && <li>{t("sharing.andMore", { count: uploadFiles.length - 20 })}</li>}
            </ul>
          </div>
          {uploadProgress && <UploadProgress progress={uploadProgress} />}
          <DialogFooter>
            <Button onClick={() => uploadMut.mutate()} disabled={uploadFiles.length === 0 || uploadMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="fs-upload-submit">
              {uploadMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {uploadMut.isPending ? t("common.uploading") : t("common.upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent data-testid="fs-new-folder-dialog">
          <DialogHeader><DialogTitle>{t("sharing.newSubfolderTitle")} {folder ? t("driveExplorer.insideX", { path: folder }) : t("sharing.inCompany", { company: activeInfo?.company_name })}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("common.folder")}</Label>
            <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder={t("sharing.folderNamePh")} data-testid="fs-new-folder-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => newFolderMut.mutate()} disabled={!newFolderName.trim() || newFolderMut.isPending} data-testid="fs-new-folder-submit">{t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!shareTarget} onOpenChange={(o) => !o && setShareTarget(null)}>
        <DialogContent data-testid="fs-share-dialog">
          <DialogHeader>
            <DialogTitle>{shareTarget?.kind === "file" ? t("sharing.shareTitleFile", { name: shareTarget?.file?.filename }) : t("sharing.shareTitleFolder", { path: shareTarget?.path })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">{t("sharing.shareNote")}</p>
            <div className="space-y-2 rounded-md border p-3">
              {companies.filter((c) => c.party_id !== activeCompany).length === 0 && <p className="text-xs text-slate-400">{t("sharing.noOtherCompanies")}</p>}
              {companies.filter((c) => c.party_id !== activeCompany).map((c) => (
                <label key={c.party_id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={sharePicks.includes(c.party_id)} onCheckedChange={() => toggleSharePick(c.party_id)} data-testid={`fs-share-pick-${c.party_id}`} />
                  <span className="font-medium">{c.company_name}</span>
                  <span className="text-xs text-slate-400">({c.role})</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => shareMut.mutate()} disabled={shareMut.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="fs-share-save">
              {shareMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("sharing.saveSharing")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FolderPickerDialog
        open={!!moveFile}
        title={`${t("common.move")} — ${moveFile?.filename || ""}`}
        folders={companyFolders}
        initial={moveFile?.folder || ""}
        rootLabel={activeInfo?.code || activeInfo?.company_name || t("folderPicker.root")}
        allowRoot
        onSubmit={(target) => moveFileMut.mutate({ id: moveFile.id, target })}
        onClose={() => setMoveFile(null)}
        isPending={moveFileMut.isPending}
        testidPrefix="fs-move"
      />
      <FolderPickerDialog
        open={bulkMoveOpen}
        title={t("driveExplorer.moveSelected", { count: selected.size })}
        folders={companyFolders}
        rootLabel={activeInfo?.code || activeInfo?.company_name || t("folderPicker.root")}
        allowRoot
        onSubmit={(target) => bulkMoveMut.mutate(target)}
        onClose={() => setBulkMoveOpen(false)}
        isPending={bulkMoveMut.isPending}
        testidPrefix="fs-bulk-move"
      />

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="fs-rename-dialog">
          <DialogHeader><DialogTitle>{renameTarget?.kind === "folder" ? t("common.renameFolder") : t("common.renameFile")}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("common.newName")}</Label>
            <Input value={renameName} onChange={(e) => setRenameName(e.target.value)} data-testid="fs-rename-input" />
          </div>
          <DialogFooter>
            <Button onClick={() => (renameTarget?.kind === "folder" ? renameFolderMut.mutate() : renameFileMut.mutate())} disabled={!renameName.trim() || renameFileMut.isPending || renameFolderMut.isPending} data-testid="fs-rename-submit">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
